/**
 * principlePrioritizationEngine.js
 *
 * Identifies the dominant pastoral principle that should guide
 * Kevia's response given the current situation.
 *
 * This is not a personality setting. It is a judgment call about which
 * pastoral value should be prioritized when multiple values are in play.
 *
 * Principles:
 *   gracia          — grace; soften, receive, not condemn
 *   verdad          — truth; speak clearly even if uncomfortable
 *   protección      — protection; safety before comfort
 *   restauración    — restoration; gentle return process
 *   paciencia       — patience; resist premature action
 *   claridad        — clarity; structure, direction, definition
 *   esperanza       — hope; anchor in what is possible
 *   responsabilidad — accountability; honest without harshness
 */

const PRINCIPLE_LABELS = {
  gracia:          "Actúa desde la gracia — acoge antes de evaluar.",
  verdad:          "La verdad pastoral importa aquí — no la evites, pero no la imposes.",
  protección:      "La seguridad y el bienestar de la persona son prioridad ahora.",
  restauración:    "Orienta hacia restauración — sin apresuramiento ni condena.",
  paciencia:       "Resiste la urgencia — este proceso necesita tiempo.",
  claridad:        "Necesita claridad pastoral — brinda estructura sin rigidez.",
  esperanza:       "Ancla la respuesta en esperanza realista y fundada.",
  responsabilidad: "Sé honesto sobre la responsabilidad — sin dureza innecesaria.",
};

/**
 * @param {{
 *   ethics:     object,
 *   emotion:    object|null,
 *   riskLevel:  string,
 *   intent:     string,
 *   hasTension: boolean,
 *   tension:    string|null,   — primary tension key
 * }} params
 * @returns {{
 *   principle:   string,
 *   instruction: string,
 * }}
 */
export function prioritizePastoralPrinciple({
  ethics,
  emotion,
  riskLevel  = "none",
  intent,
  hasTension = false,
  tension    = null,
}) {
  const detected = ethics?.detected ?? [];
  const dom      = emotion?.dominantEmotion ?? "none";

  // Protection always wins at critical/high risk with direct harm signals
  if (
    riskLevel === "critical" ||
    (riskLevel === "high" && detected.includes("abuse"))
  ) {
    return { principle: "protección", instruction: PRINCIPLE_LABELS.protección };
  }

  // Restoration when discipline or apostasy is present but emotion is grief/shame (not defiant)
  if (
    (detected.includes("discipline") || detected.includes("apostasy")) &&
    (dom === "grief" || dom === "shame" || dom === "spiritual_confusion")
  ) {
    return { principle: "restauración", instruction: PRINCIPLE_LABELS.restauración };
  }

  // Accountability when high risk + discipline + no emotional fragility
  if (
    riskLevel === "high" &&
    detected.includes("discipline") &&
    dom !== "grief" && dom !== "shame"
  ) {
    return { principle: "responsabilidad", instruction: PRINCIPLE_LABELS.responsabilidad };
  }

  // Truth when tension is verdad_compasion (truth is being avoided)
  if (hasTension && tension === "verdad_compasion") {
    return { principle: "verdad", instruction: PRINCIPLE_LABELS.verdad };
  }

  // Hope when grief or crisis without high-risk escalation
  if (dom === "grief" && riskLevel !== "high" && riskLevel !== "critical") {
    return { principle: "esperanza", instruction: PRINCIPLE_LABELS.esperanza };
  }

  // Patience when urgencia_paciencia tension is active
  if (hasTension && tension === "urgencia_paciencia") {
    return { principle: "paciencia", instruction: PRINCIPLE_LABELS.paciencia };
  }

  // Clarity for planning/analysis intent with little emotional charge
  if (
    (intent === "planning" || intent === "analysis" || intent === "sermon") &&
    dom === "none"
  ) {
    return { principle: "claridad", instruction: PRINCIPLE_LABELS.claridad };
  }

  // Grace as the default when shame or spiritual confusion with no urgent risk
  if (dom === "shame" || dom === "spiritual_confusion") {
    return { principle: "gracia", instruction: PRINCIPLE_LABELS.gracia };
  }

  // Default: grace
  return { principle: "gracia", instruction: PRINCIPLE_LABELS.gracia };
}
